import { Link } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { planSettingsPath } from "@/react/helpers/urlHelpers";
import { usePunditUserContext } from "@circle-react/contexts";
import { getNextTier, isPlanBasic } from "@circle-react/helpers/planHelpers";
import { CurrencySelector } from "@circle-react-shared/Billing/CurrencySelector";
import { Button } from "@circle-react-uikit/Button";
import { Modal } from "@circle-react-uikit/ModalV2";
import { Typography } from "@circle-react-uikit/Typography";
import { useUpgradePlanContext } from "./../UpgradePlanContext";
import { PlanFeaturesList } from "./PlanFeaturesList";
import { PlanLineItems } from "./PlanLineItems";
import { getDisplayName, getDisplaySubheading } from "./helpers";

const i18nRoot = "pricing_plans.upgrade_modal";

interface UpgradePlanDetailsProps {
  hideModal: () => void;
  onSubmitHandler: () => void;
  subheading?: string;
}

const getEverythingInCopy = (
  isEmailHubOnlyUpgrade: boolean,
  nextTier: string | undefined,
  planTier: string | undefined,
): string => {
  if (isEmailHubOnlyUpgrade) {
    return "email_hub_features_title";
  }
  if (nextTier === planTier) {
    return `everything_in_${planTier}`;
  }
  return "everything_in_your_plan_plus";
};

export const UpgradePlanDetails = ({
  subheading = "",
  hideModal,
  onSubmitHandler,
}: UpgradePlanDetailsProps) => {
  const {
    billingCurrency,
    setBillingCurrency,
    plan,
    emailHubPlan,
    shouldShowCurrencySelection,
    isEmailHubOnlyUpgrade,
    isDoubleUpgrade,
  } = useUpgradePlanContext();

  const { currentCommunity } = usePunditUserContext();
  const { current_plan_tier: currentPlanTier } = currentCommunity || {};

  const learnMoreRedirect = (): void => {
    window.location.href = planSettingsPath();
  };

  const nextTier = currentPlanTier && getNextTier(currentPlanTier);
  const displayName = getDisplayName(plan, emailHubPlan);
  const everythingInCopy = getEverythingInCopy(
    isEmailHubOnlyUpgrade,
    nextTier || "",
    plan?.tier || "",
  );

  const shouldShowUpgradeFeatureList = !isPlanBasic(plan?.tier);

  const featuresToDisplay =
    plan?.main_features || emailHubPlan?.main_features || [];

  const displaySubheading = getDisplaySubheading(
    subheading,
    isEmailHubOnlyUpgrade,
  );

  return (
    <>
      <Modal.Header>
        <Modal.Title>
          {isEmailHubOnlyUpgrade
            ? t([i18nRoot, "upgrade_title_email_hub_only"])
            : t([i18nRoot, "upgrade_title"], {
                plan: displayName,
              })}
        </Modal.Title>

        <Modal.HeaderActions>
          <Modal.CloseButton onClick={hideModal} />
        </Modal.HeaderActions>
      </Modal.Header>
      <Modal.Body>
        {displaySubheading && (
          <div className="mb-6">
            <Typography.BodyMd color="text-default">
              {displaySubheading}
            </Typography.BodyMd>
          </div>
        )}

        <PlanLineItems />

        {shouldShowCurrencySelection && (
          <CurrencySelector
            billingCurrency={billingCurrency ?? null}
            onCurrencyChange={setBillingCurrency}
            className="bg-tertiary mt-2"
          />
        )}

        {!isDoubleUpgrade && shouldShowUpgradeFeatureList && (
          <div className="mt-6">
            <Typography.LabelLg weight="semibold">
              {t([i18nRoot, everythingInCopy])}
            </Typography.LabelLg>
            <div className="mt-3">
              <PlanFeaturesList
                features={featuresToDisplay}
                iconType="16-star-fill"
                iconColor="text-blue-highlight"
              />
            </div>
            {!isEmailHubOnlyUpgrade && (
              <div className="mt-5">
                <Link to={planSettingsPath()} onClick={learnMoreRedirect}>
                  <Typography.LabelMd weight="medium" color="text-default">
                    {t("pricing_plans.upgrade_modal.learn_more")}
                  </Typography.LabelMd>
                </Link>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="mt-5 flex justify-end">
        <Button variant="circle" large onClick={onSubmitHandler}>
          {isEmailHubOnlyUpgrade
            ? t([i18nRoot, "actions.enable_email_hub"])
            : t([i18nRoot, "actions.upgrade"], { plan: displayName })}
        </Button>
      </Modal.Footer>
    </>
  );
};
